/*
 * Release notes - the rules a release note's own fields obey (S42, 2026-10-12).
 *
 * The owner's request: a release note is a Title, a Content, a Product and a
 * Version, published like an article but kept apart from articles. Content is
 * markdown with no rule of its own beyond the body cap (contracts.ts), and the
 * product is the article catalogue's (kb.Product). The other two fields have
 * rules the database also enforces, and this module is the second
 * implementation of them - the same arrangement slug.ts has with the slug CHECKs
 * and articleImages.ts with CK_ArticleImage_FileName. The database stays the
 * authority: two editors racing to the same version are stopped by
 * UQ_ReleaseNote_ProductId_Version, not by anything here. What this file buys is
 * that a rule the browser can evaluate is explained under the input that broke
 * it, instead of arriving as a constraint error after the save.
 *
 * The version
 * -----------
 * The owner's syntax is "v<x>.<y>.<z>.<a>", e.g. v1.2.3.4, and the version is
 * half of the note's ADDRESS: /releasenotes/<productSlug>/<version>, one note per
 * product and version, with no slug of its own. So the grammar is decided here
 * rather than left loose, and every decision is about keeping that address
 * unambiguous:
 *
 *   - a LOWERCASE v, never V. One spelling per address; the editor's input pins
 *     the v so an author never types it (normaliseReleaseNoteVersionInput)
 *   - exactly four parts, each a whole number in ASCII digits
 *   - no leading zeros: v1.0.87.3 and v1.0.087.3 would otherwise be two rows of
 *     UQ_ReleaseNote_ProductId_Version that a reader cannot tell apart
 *   - at most nine digits a part, so each part fits an INT should the database
 *     ever need to sort numerically, and the whole is at most 1 + 4*9 + 3 = 40
 *     characters - kb.ReleaseNote.Version is VARCHAR(40)
 *
 * RELEASE_NOTE_VERSION_PATTERN mirrors CK_ReleaseNote_Version, in
 * Tables/kb.ReleaseNote.sql, term for term. T-SQL has no regular expressions, so
 * the CHECK spells the same grammar with LIKE, every comparison under
 * Latin1_General_100_BIN2 so that [0-9] and 'v' mean code points rather than
 * whatever the collation sorts near them:
 *
 *     ^v and a digit opening each part   Version LIKE 'v[0-9]%.[0-9]%.[0-9]%.[0-9]%'
 *     ASCII digits and dots only         SUBSTRING(Version, 2, 40) NOT LIKE '%[^0-9.]%'
 *     exactly four parts                 LEN(Version) - LEN(REPLACE(Version, '.', '')) = 3
 *     0 | [1-9]...  (no leading zero)    NOT LIKE 'v0[0-9]%' AND NOT LIKE '%.0[0-9]%'
 *     {0,8} after the first digit        NOT LIKE ten consecutive [0-9]
 *     $ (nothing after the last digit)   DATALENGTH(Version) = LEN(Version)
 *
 * The last term is there because LEN and = both ignore trailing spaces in T-SQL,
 * and the regex's $ does not. The two must accept exactly the same strings; a
 * change to either side is a change to both.
 *
 * Rejected: a semver-shaped grammar (three parts, pre-release suffixes). The
 * owner asked for four numbers, the products are versioned that way, and every
 * optional part would be one more way to write the same address twice.
 * Rejected: accepting V and lowercasing it on the server. The ingest API would
 * then answer a different version from the one it was sent; refusing is what
 * tells a caller its spelling is not the address.
 *
 * The title
 * ---------
 * NVARCHAR(200), not blank, and ONE LINE: the owner's "single line of text".
 * kb.ReleaseNoteRevision refuses a carriage return or a line feed in it
 * (CK_ReleaseNoteRevision_Title), and isSingleLineTitle is that rule - CR and LF
 * and nothing more, because a broader idea of a line break here (U+2028, U+0085)
 * would refuse a title the database accepts, which is the drift this file
 * exists to prevent. The article title is deliberately different (300, and
 * newlines tolerated), which is why contracts.ts gives release notes a title
 * schema of their own rather than reusing the article's.
 *
 * Images
 * ------
 * Release-note images follow every article-image rule (5 MB, the four raster
 * formats, never SVG, the file-name grammar, sniffed bytes) and differ only in
 * where they hang and how many may: their own table, kb.ReleaseNoteImage, and
 * their own ceiling, mirrored by kb.TR_ReleaseNoteImage_MaxPerReleaseNote. It is
 * twenty, the same number as an article's, but a constant of its own with a
 * message of its own - sharing ARTICLE_IMAGE_MAX_PER_ARTICLE would tie two
 * triggers to one number nobody decided they should share, and an author of a
 * release note told that "an article can carry at most 20 images" has been told
 * about the wrong thing.
 *
 * Everything here is pure: no I/O, no DOM, no SQL. It is shared by the server
 * (which validates and binds) and the browser (which pins the v and explains).
 */
/** kb.ReleaseNoteRevision.Title is NVARCHAR(200). */
export const RELEASE_NOTE_TITLE_MAX_LENGTH = 200;
/** The fixed first character of every version, and the one the editor's input pins. */
export const RELEASE_NOTE_VERSION_PREFIX = 'v';
/** kb.ReleaseNote.Version is VARCHAR(40): the v, four nine-digit parts and three dots. */
export const RELEASE_NOTE_VERSION_MAX_LENGTH = 40;
/**
 * The version grammar, mirroring CK_ReleaseNote_Version term for term (see the
 * header): a lowercase v and four dot-separated whole numbers, each `0` or one to
 * nine ASCII digits with no leading zero. No flags - in particular no `i`, since
 * `V1.2.3.4` is a different string that the CHECK refuses.
 */
export const RELEASE_NOTE_VERSION_PATTERN = /^v(0|[1-9][0-9]{0,8})\.(0|[1-9][0-9]{0,8})\.(0|[1-9][0-9]{0,8})\.(0|[1-9][0-9]{0,8})$/;
/**
 * At most 20 images on one release note, matching
 * kb.TR_ReleaseNoteImage_MaxPerReleaseNote. Its own constant, not
 * ARTICLE_IMAGE_MAX_PER_ARTICLE: the same number by decision, not by coupling.
 */
export const RELEASE_NOTE_IMAGE_MAX_PER_NOTE = 20;
/**
 * Whether a value is a version the database would store. Exact: no trimming, so
 * a caller that wants `' v1.2.3.4 '` accepted trims first, as the zod schema does.
 */
export function isValidReleaseNoteVersion(value) {
    return (typeof value === 'string' &&
        value.length <= RELEASE_NOTE_VERSION_MAX_LENGTH &&
        RELEASE_NOTE_VERSION_PATTERN.test(value));
}
/** The message shown next to a version the grammar refuses, in the field's voice. */
export function describeReleaseNoteVersionRequirement() {
    return ('Use v and four whole numbers separated by dots, for example v1.2.3.4 - ' +
        'no leading zeros, and at most 9 digits each.');
}
/**
 * The four numbers of a valid version, or null when the value is not one.
 * Null rather than a best effort: a string the grammar refuses names no stored
 * note, so there is nothing sensible to compare it as.
 */
export function parseReleaseNoteVersion(value) {
    if (!isValidReleaseNoteVersion(value))
        return null;
    const match = RELEASE_NOTE_VERSION_PATTERN.exec(value);
    if (!match)
        return null;
    return [Number(match[1]), Number(match[2]), Number(match[3]), Number(match[4])];
}
/**
 * Orders two versions NUMERICALLY, part by part, as an `Array.prototype.sort`
 * comparator: negative when `a` is the older, positive when it is the newer,
 * zero when they are the same version. So v1.10.0.0 sorts after v1.9.0.0, which
 * a string comparison gets backwards.
 *
 * Total, so a sort never misbehaves on bad data: a value that is not a valid
 * version sorts before every valid one, and two invalid values fall back to
 * plain code-unit order. The public list is ordered by publish date, not by this
 * (the owner's answer, 2026-10-12); this is for the places that list one
 * product's notes by version.
 */
export function compareReleaseNoteVersions(a, b) {
    const left = parseReleaseNoteVersion(a);
    const right = parseReleaseNoteVersion(b);
    if (left && right) {
        for (let index = 0; index < 4; index += 1) {
            const difference = (left[index] ?? 0) - (right[index] ?? 0);
            if (difference !== 0)
                return difference < 0 ? -1 : 1;
        }
        return 0;
    }
    if (left)
        return 1;
    if (right)
        return -1;
    return a < b ? -1 : a > b ? 1 : 0;
}
/**
 * What the editor's version input runs on every change: removes all whitespace
 * and EVERY `v` or `V`, then puts exactly one lowercase `v` in front. So an empty
 * field reads `v`, and typing, or pasting, `v1.2.3.4`, `V1.2.3.4` or `1.2.3.4`
 * all give `v1.2.3.4` - the prefix cannot be deleted and cannot be doubled.
 *
 * Every v, not only the leading ones: a version contains no v but the prefix, so
 * one typed anywhere else is a slip the input absorbs rather than a character
 * the author meant. Nothing else is removed or repaired -
 * a comma, a letter or a leading zero stays in place for the grammar to refuse
 * under the field, where the author can see what to fix.
 *
 * Not used on the server: an API caller's version is validated as sent (after a
 * trim), never rewritten into a different address.
 */
export function normaliseReleaseNoteVersionInput(raw) {
    return RELEASE_NOTE_VERSION_PREFIX + raw.replace(/[\svV]/g, '');
}
/**
 * Whether a title is one line: no carriage return and no line feed, which is
 * exactly what CK_ReleaseNoteRevision_Title refuses. See the header for why
 * other line separators are left alone.
 */
export function isSingleLineTitle(value) {
    return !/[\r\n]/.test(value);
}
/** The message shown when a release note already carries as many images as it may. */
export function describeReleaseNoteImageCountLimit() {
    return (`A release note can carry at most ${RELEASE_NOTE_IMAGE_MAX_PER_NOTE} images. ` +
        'Delete one before attaching another, or replace an existing one.');
}
/**
 * How a release note is named where one must be identified in a few words: the
 * delete dialog's heading and its typed confirmation, on the server (the
 * impact's `targetLabel`) and in the browser (the dialog's target) alike, so the
 * two can never disagree - `accoPLANNING v1.0.87.3`.
 *
 * Product and version rather than the title, because they are the note's
 * address: unique together, always present (an unwritten note has no title) and
 * short, where a title can run to 200 characters and repeat across notes.
 */
export function releaseNoteLabel(productName, version) {
    return `${productName} ${version}`;
}
//# sourceMappingURL=releaseNotes.js.map